// src/pages/menu/LocationWaitersDashboard.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  Fade,
  Badge,
  Avatar,
  useTheme,
  alpha,
  Pagination,
  CircularProgress,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Tooltip,
  Paper,
  LinearProgress,
  useMediaQuery,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingCart as CartIcon,
  RestaurantMenu as MenuIcon,
  LocalDining as FoodIcon,
  LocalDrink as DrinkIcon,
  Delete as DeleteIcon,
  Restaurant as RestaurantIcon,
  CheckCircle as CheckCircleIcon,
  Phone as PhoneIcon,
  TableRestaurant as TableRestaurantIcon,
  Send as SendIcon,
  Print as PrintIcon,
  Kitchen as KitchenIcon,
  LocalBar as LocalBarIcon,
  Receipt as ReceiptIcon,
  People as PeopleIcon,
  LockClock as LockClockIcon,
} from '@mui/icons-material';

// Import components
import Header from '../../../components/RestaurantPOS/waiters/Header';
import Footer from '../../../components/RestaurantPOS/waiters/Footer';
import Preloader from '../../../theme/Preloader';
import restaurantConfig from '../../../config/Organisation_Details';
import '../../../styles/Waiters.css';
import '../../../styles/ThermalReceipt.css';
import foodImage from '../../../assets/f&b.jpg';

// ============================================
// CONSTANTS & CONFIGURATION
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/pos_v1';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

const ITEMS_PER_PAGE = 12;
const CARD_WIDTH = 240;
const CARD_HEIGHT = 210;

// ============================================
// UTILITY FUNCTIONS
// ============================================

const formatCurrency = (amount, currency = '₦') => {
  return `${currency} ${Number(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// ============================================
// TOKEN & AUTH FUNCTIONS
// ============================================

const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    const payload = token.split('.')[1];
    if (!payload) return true;
    const decoded = JSON.parse(atob(payload));
    if (!decoded.exp) return true;
    return decoded.exp * 1000 < Date.now();
  } catch (err) {
    console.error('[MenuPOS] Error checking token expiry:', err);
    return true;
  }
};

const getTokenExpiryTime = (token) => {
  try {
    if (!token) return 0;
    const payload = token.split('.')[1];
    if (!payload) return 0;
    const decoded = JSON.parse(atob(payload));
    return decoded.exp ? decoded.exp * 1000 : 0;
  } catch (err) {
    console.error('[MenuPOS] Error getting token expiry:', err);
    return 0;
  }
};

// ============================================
// API HELPERS
// ============================================

const refreshAuthToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      console.warn('[MenuPOS] No refresh token available');
      throw new Error('No refresh token available');
    }

    console.debug('[MenuPOS] Refreshing auth token...');
    const response = await fetch(`${FULL_API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      throw new Error(`Refresh failed with status: ${response.status}`);
    }

    const data = await response.json();
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
      console.debug('[MenuPOS] Token refreshed successfully');
      return data.access_token;
    }
    throw new Error('No access token in refresh response');
  } catch (err) {
    console.error('[MenuPOS] Token refresh failed:', err.message);
    localStorage.clear();
    window.location.href = '/restaurantAuth';
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  let token = localStorage.getItem('access_token');
  
  if (!token || isTokenExpired(token)) {
    console.debug('[MenuPOS] Token invalid, attempting refresh...');
    token = await refreshAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }
  }

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };

  const url = `${FULL_API_URL}${endpoint}`;
  console.debug(`[MenuPOS] API Request: ${options.method || 'GET'} ${url}`);

  try {
    const response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
      console.debug('[MenuPOS] Received 401, attempting token refresh...');
      const newToken = await refreshAuthToken();
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        const retryResponse = await fetch(url, { ...options, headers });
        if (retryResponse.ok) {
          const contentType = retryResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            return await retryResponse.json();
          }
          return { message: await retryResponse.text() };
        }
        if (retryResponse.status === 401) {
          throw new Error('Authentication failed after refresh');
        }
      }
      throw new Error('Authentication failed');
    }

    const contentType = response.headers.get('content-type');
    let data;
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      data = { message: text || 'Non-JSON response received' };
    }

    if (!response.ok) {
      const errorMessage = data.detail || data.message || `HTTP ${response.status}`;
      throw new Error(errorMessage);
    }

    return data;
  } catch (err) {
    console.error('[MenuPOS] API Error:', err.message);
    throw err;
  }
};

// ============================================
// LOCATION CONFIGURATION
// ============================================

const LOCATION_CONFIG = {
  african: {
    id: 5,
    name: 'African',
    color: '#2E7D32',
    accentColor: '#C62828',
    lightColor: '#4CAF50',
    icon: '🌍',
    path: '/african-menu'
  },
  continental: {
    id: 3,
    name: 'Continental',
    color: '#1565C0',
    accentColor: '#C62828',
    lightColor: '#1976D2',
    icon: '🌐',
    path: '/continental-menu'
  },
  lounge: {
    id: 4,
    name: 'Lounge',
    color: '#6A1B9A',
    accentColor: '#C62828',
    lightColor: '#9C27B0',
    icon: '🍸',
    path: '/lounge-menu'
  }
};

// ============================================
// MEMOIZED CART ITEM COMPONENT WITH STOCK VALIDATION
// ============================================

const CartItem = memo(({ item, onUpdateQuantity, onRemove, locationColor, stockStatus, setSnackbar }) => {
  const updateQuantity = useCallback((quantity) => {
    // Check if trying to add more than available
    if (quantity > item.quantity && stockStatus && stockStatus.quantity !== null) {
      // Calculate total quantity in cart
      const totalAfterChange = quantity;
      if (totalAfterChange > stockStatus.quantity) {
        setSnackbar({
          open: true,
          message: `Only ${stockStatus.quantity} ${stockStatus.unit || 'unit'}${stockStatus.quantity > 1 ? 's' : ''} of ${item.name} available. You already have ${item.quantity} in cart.`,
          severity: 'warning',
        });
        return;
      }
    }
    onUpdateQuantity(item.id, quantity);
  }, [item.id, item.name, item.quantity, onUpdateQuantity, stockStatus, setSnackbar]);

  const removeItem = useCallback(() => {
    onRemove(item.id);
  }, [item.id, onRemove]);

  const isLowStock = stockStatus && stockStatus.is_low_stock && !stockStatus.is_out_of_stock;
  const isOutOfStock = stockStatus && stockStatus.is_out_of_stock;
  const remainingStock = stockStatus?.quantity || 0;

  return (
    <Box
      sx={{
        mb: { xs: 1, md: 1.5 },
        bgcolor: isOutOfStock ? alpha('#f44336', 0.08) : 
                  isLowStock ? alpha('#ff9800', 0.05) : 
                  alpha(locationColor, 0.03),
        borderRadius: '8px',
        border: `1px solid ${isOutOfStock ? alpha('#f44336', 0.4) : 
                isLowStock ? alpha('#ff9800', 0.3) : 
                alpha('#000', 0.04)}`,
        '&:hover': { bgcolor: isOutOfStock ? alpha('#f44336', 0.12) : 
                      isLowStock ? alpha('#ff9800', 0.08) : 
                      alpha(locationColor, 0.06) },
        p: { xs: 0.75, md: 1.5 },
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 0.5, md: 0.75 },
      }}
    >
      {/* Stock status indicators */}
      {isOutOfStock && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 0.5, 
            py: 0.25, 
            fontSize: '0.7rem',
            '& .MuiAlert-icon': { fontSize: '0.8rem', alignItems: 'center' },
            '& .MuiAlert-message': { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }
          }}
          action={
            <Button 
              color="error" 
              size="small" 
              onClick={removeItem}
              sx={{ fontSize: '0.6rem', textTransform: 'none', fontWeight: 600 }}
            >
              Remove
            </Button>
          }
        >
          OUT OF STOCK - Please remove this item
        </Alert>
      )}

      {/* Low stock warning with remaining count */}
      {isLowStock && !isOutOfStock && (
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 0.5, 
            py: 0.25, 
            fontSize: '0.7rem',
            '& .MuiAlert-icon': { fontSize: '0.8rem', alignItems: 'center' }
          }}
        >
          Only {remainingStock} left in stock! {item.quantity > remainingStock ? 'Reduce quantity.' : ''}
        </Alert>
      )}

      {/* In-stock but limited warning */}
      {!isOutOfStock && !isLowStock && remainingStock > 0 && remainingStock <= 10 && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 0.5, 
            py: 0.25, 
            fontSize: '0.7rem',
            '& .MuiAlert-icon': { fontSize: '0.8rem', alignItems: 'center' }
          }}
        >
          {remainingStock} units remaining in stock
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1 } }}>
        <Avatar sx={{
          bgcolor: isOutOfStock ? alpha('#f44336', 0.1) : alpha(locationColor, 0.1),
          color: isOutOfStock ? '#f44336' : locationColor,
          width: { xs: 24, md: 32 },
          height: { xs: 24, md: 32 },
          fontSize: { xs: '0.6rem', md: '0.9rem' },
          flexShrink: 0,
        }}>
          {item.main_category === 'Food' ? <RestaurantIcon fontSize="small" /> : <DrinkIcon fontSize="small" />}
        </Avatar>
        
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" fontWeight={600} fontSize={{ xs: '0.85rem', md: '1rem' }} noWrap>
            {item.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" fontWeight={500} fontSize={{ xs: '0.75rem', md: '0.85rem' }}>
            {formatCurrency(item.amount)}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.3, md: 0.5 }, flexShrink: 0 }}>
          <IconButton
            size="small"
            onClick={() => updateQuantity(item.quantity - 1)}
            disabled={isOutOfStock || item.quantity <= 1}
            sx={{
              bgcolor: alpha(locationColor, 0.08),
              '&:hover': { bgcolor: alpha(locationColor, 0.15) },
              width: { xs: 16, md: 24 },
              height: { xs: 16, md: 24 },
              '&.Mui-disabled': { opacity: 0.3 }
            }}
          >
            <RemoveIcon fontSize="small" sx={{ fontSize: { xs: '0.5rem', md: '0.9rem' } }} />
          </IconButton>
          
          <Typography variant="body1" fontWeight={700} sx={{ 
            minWidth: 16, 
            textAlign: 'center', 
            fontSize: { xs: '0.6rem', md: '0.85rem' },
            color: isOutOfStock ? 'text.disabled' : 'inherit'
          }}>
            {item.quantity}
          </Typography>
          
          <IconButton
            size="small"
            onClick={() => updateQuantity(item.quantity + 1)}
            disabled={isOutOfStock || (item.quantity >= remainingStock && remainingStock > 0)}
            sx={{
              bgcolor: alpha(locationColor, 0.08),
              '&:hover': { bgcolor: alpha(locationColor, 0.15) },
              width: { xs: 16, md: 24 },
              height: { xs: 16, md: 24 },
              '&.Mui-disabled': { opacity: 0.3 }
            }}
          >
            <AddIcon fontSize="small" sx={{ fontSize: { xs: '0.5rem', md: '0.9rem' } }} />
          </IconButton>
          
          <IconButton
            edge="end"
            onClick={removeItem}
            color="error"
            size="small"
            sx={{
              bgcolor: alpha('#f44336', 0.1),
              '&:hover': { bgcolor: alpha('#f44336', 0.2) },
              width: { xs: 20, md: 28 },
              height: { xs: 20, md: 28 },
            }}
          >
            <DeleteIcon fontSize="small" sx={{ fontSize: { xs: '0.5rem', md: '0.9rem' } }} />
          </IconButton>
        </Box>
      </Box>
      
      {/* Show max available indicator */}
      {!isOutOfStock && remainingStock > 0 && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          mt: 0.5,
          fontSize: '0.6rem',
          color: 'text.secondary'
        }}>
          {item.quantity >= remainingStock && (
            <Typography variant="caption" color="warning.main" fontWeight={600}>
              Max quantity reached
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
});

CartItem.displayName = 'CartItem';

// ============================================
// MEMOIZED MENU ITEM COMPONENT WITH STOCK STATUS
// ============================================

const MenuItemCard = memo(({ 
  item, 
  onAddToCart, 
  locationColor, 
  accentColor, 
  theme, 
  isMobile, 
  stockStatus, 
  setSnackbar,
  isStockStatusLoaded,  // NEW: Track if stock status has been loaded
  isStockStatusLoading  // NEW: Track if stock status is currently loading
}) => {
  const handleAdd = useCallback(() => {
    // CRITICAL: If stock status hasn't loaded yet, prevent adding
    if (!isStockStatusLoaded) {
      setSnackbar({
        open: true,
        message: 'Stock information is still loading. Please wait...',
        severity: 'info',
      });
      return;
    }
    
    // If stock is currently loading, prevent adding
    if (isStockStatusLoading) {
      setSnackbar({
        open: true,
        message: 'Checking stock availability...',
        severity: 'info',
      });
      return;
    }
    
    // Check if item is out of stock
    if (stockStatus && stockStatus.is_out_of_stock) {
      setSnackbar({
        open: true,
        message: `${item.name} is out of stock!`,
        severity: 'error',
      });
      return;
    }
    
    // Check if stock status is known and item is out of stock
    if (stockStatus && stockStatus.status === 'OUT_OF_STOCK') {
      setSnackbar({
        open: true,
        message: `${item.name} is out of stock!`,
        severity: 'error',
      });
      return;
    }
    
    onAddToCart(item);
  }, [item, onAddToCart, stockStatus, setSnackbar, isStockStatusLoaded, isStockStatusLoading]);

  // Determine stock status with proper fallbacks
  const isOutOfStock = stockStatus && (stockStatus.is_out_of_stock || stockStatus.status === 'OUT_OF_STOCK');
  const isLowStock = stockStatus && stockStatus.is_low_stock && !isOutOfStock;
  const isTracked = stockStatus && stockStatus.status !== 'NOT_TRACKED';
  
  // CRITICAL: Disable Add button if stock status hasn't loaded yet
  const shouldDisable = !isStockStatusLoaded || isStockStatusLoading || isOutOfStock || (item.is_active !== 1 && item.is_active !== true);

  return (
    <Card sx={{
      height: '100%',
      minHeight: isMobile ? 160 : CARD_HEIGHT,
      display: 'flex',
      flexDirection: 'column',
      borderRadius: '12px',
      position: 'relative',
      overflow: 'visible',
      bgcolor: isOutOfStock ? 'rgba(244, 67, 54, 0.04)' : 'white',
      border: `1px solid ${isOutOfStock ? alpha('#f44336', 0.3) : alpha(locationColor, 0.08)}`,
      width: '100%',
      '&:hover': {
        boxShadow: theme.shadows[6],
        transform: 'translateY(-2px)',
        transition: 'all 0.2s ease-in-out',
      },
    }}>
      {/* Category Badge */}
      <Box sx={{
        position: 'absolute',
        top: -6,
        right: 12,
        bgcolor: accentColor,
        color: 'white',
        fontWeight: 600,
        fontSize: { xs: '0.4rem', sm: '0.55rem' },
        zIndex: 1,
        borderRadius: '4px',
        px: { xs: 0.75, sm: 1.5 },
        py: 0.5,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        maxWidth: '80%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {item.category || 'Uncategorized'}
      </Box>

      {/* Stock Status Badge */}
      {isTracked && (
        <Box sx={{
          position: 'absolute',
          bottom: 55,
          right: 8,
          zIndex: 1,
        }}>
          {!isStockStatusLoaded ? (
            <Chip
              label="Loading..."
              size="small"
              sx={{
                bgcolor: '#9e9e9e',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.4rem',
                height: '16px',
                '& .MuiChip-label': { px: 0.75 }
              }}
            />
          ) : isOutOfStock ? (
            <Chip
              label="OUT OF STOCK"
              size="small"
              sx={{
                bgcolor: '#f44336',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.5rem',
                height: '16px',
                '& .MuiChip-label': { px: 0.75 }
              }}
            />
          ) : isLowStock ? (
            <Chip
              label={`${Math.round(stockStatus.quantity)} left`}
              size="small"
              sx={{
                bgcolor: '#ff9800',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.5rem',
                height: '16px',
                '& .MuiChip-label': { px: 0.75 }
              }}
            />
          ) : isStockStatusLoading ? (
            <Chip
              label="Checking..."
              size="small"
              sx={{
                bgcolor: '#9e9e9e',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.4rem',
                height: '16px',
                '& .MuiChip-label': { px: 0.75 }
              }}
            />
          ) : null}
        </Box>
      )}
      
      <CardContent sx={{
        flex: 1,
        pt: { xs: 1.5, sm: 2 },
        pb: { xs: 0.5, sm: 1 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        px: { xs: 0.75, sm: 1.5 },
      }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          py: 0.5,
        }}>
          <img 
            src={foodImage}
            alt="Food item"
            style={{
              width: isMobile ? 55 : 80,
              height: isMobile ? 55 : 80,
              objectFit: 'contain',
              opacity: isOutOfStock ? 0.5 : 0.85,
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
            }}
            loading="lazy"
          />
        </Box>
        
        <Typography variant="h6" sx={{
          fontWeight: 700,
          fontSize: { xs: '0.6rem', sm: '0.8rem' },
          textAlign: 'center',
          wordWrap: 'break-word',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          maxWidth: '100%',
          lineHeight: 1.2,
          minHeight: { xs: '2em', sm: '2.4em' },
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          width: '100%',
          px: 0.5,
          color: isOutOfStock ? 'text.disabled' : 'text.primary',
        }}>
          {item.name}
        </Typography>
        
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          pt: 0.5,
          borderTop: `1px solid ${alpha('#000', 0.06)}`,
          mt: 'auto',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
            {item.main_category === 'Food' ? (
              <RestaurantIcon fontSize="small" sx={{ color: accentColor, fontSize: { xs: '0.5rem', sm: '0.7rem' } }} />
            ) : item.main_category === 'Drinks' ? (
              <DrinkIcon fontSize="small" sx={{ color: '#1976D2', fontSize: { xs: '0.5rem', sm: '0.7rem' } }} />
            ) : (
              <FoodIcon fontSize="small" sx={{ color: locationColor, fontSize: { xs: '0.5rem', sm: '0.7rem' } }} />
            )}
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.4rem', sm: '0.55rem' } }}>
              {item.main_category || 'Food'}
            </Typography>
          </Box>
          <Typography variant="h6" sx={{
            fontWeight: 700,
            color: isOutOfStock ? 'text.disabled' : locationColor,
            fontSize: { xs: '0.65rem', sm: '0.85rem' },
          }}>
            {formatCurrency(item.amount)}
          </Typography>
        </Box>
      </CardContent>
      
      <CardActions sx={{ p: { xs: 0.5, sm: 1 }, pt: 0 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={handleAdd}
          disabled={shouldDisable}
          sx={{
            bgcolor: shouldDisable ? 'grey.400' : locationColor,
            '&:hover': {
              bgcolor: shouldDisable ? 'grey.400' : locationColor,
              opacity: 0.9,
            },
            '&:disabled': {
              bgcolor: 'grey.300',
            },
            borderRadius: '8px',
            py: { xs: 0.25, sm: 0.5 },
            textTransform: 'none',
            fontWeight: 600,
            fontSize: { xs: '0.55rem', sm: '0.7rem' },
          }}
          startIcon={!isStockStatusLoaded || isStockStatusLoading ? 
            <CircularProgress size={12} color="inherit" /> : 
            <AddIcon sx={{ fontSize: { xs: '0.6rem', sm: '0.85rem' } }} />
          }
        >
          {!isStockStatusLoaded ? 'Loading stock...' :
           isStockStatusLoading ? 'Checking...' :
           isOutOfStock ? 'Out of Stock' : 
           (item.is_active !== 1 && item.is_active !== true) ? 'Unavailable' : 
           'Add'}
        </Button>
      </CardActions>
    </Card>
  );
});

MenuItemCard.displayName = 'MenuItemCard';

// ============================================
// MAIN COMPONENT
// ============================================

const LocationWaitersDashboard = ({ locationKey = 'african' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Get location config
  const locationConfig = LOCATION_CONFIG[locationKey] || LOCATION_CONFIG.african;
  const LOCATION_COLOR = locationConfig.color;
  const ACCENT_COLOR = locationConfig.accentColor;
  const LOCATION_NAME = locationConfig.name;
  const LOCATION_ID = locationConfig.id;
  
  // ============================================
  // STATE MANAGEMENT
  // ============================================
  
  const [activeMenu, setActiveMenu] = useState('Menu');
  const [displayText, setDisplayText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);

  // Location Status
  const [locationStatus, setLocationStatus] = useState({
    can_take_orders: true,
    is_day_closed: false,
    has_active_shift: false,
    status_message: '',
    loading: true
  });

  // Stock status states
  const [stockStatusMap, setStockStatusMap] = useState({});
  const [fetchingStockStatus, setFetchingStockStatus] = useState(false);
  const [isStockStatusLoaded, setIsStockStatusLoaded] = useState(false); // NEW: Track if stock status has been loaded at least once

  // Data states
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tables, setTables] = useState([]);
  const [loadingTables, setLoadingTables] = useState(false);
  
  // Filter states
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  
  // Cart states
  const [cartItems, setCartItems] = useState([]);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [hasOutOfStockInCart, setHasOutOfStockInCart] = useState(false);
  
  // Order dialog states
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orderType, setOrderType] = useState('DINE_IN');
  const [selectedTable, setSelectedTable] = useState(null);
  const [customerPhone, setCustomerPhone] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);
  
  // Order totals
  const [orderTotalItems, setOrderTotalItems] = useState(0);
  const [orderTotalAmount, setOrderTotalAmount] = useState(0);
  
  // Receipt states
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printProgress, setPrintProgress] = useState(0);
  const [kotTicketData, setKotTicketData] = useState(null);
  const [botTicketData, setBotTicketData] = useState(null);

  // UI states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // ============================================
  // REFS
  // ============================================
  
  const typingTimeoutRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const statusCheckIntervalRef = useRef(null);
  
  // ============================================
  // CONFIG
  // ============================================

  const DISPLAY_NAMES = {
    african: restaurantConfig.name || 'African Restaurant',
    continental: restaurantConfig.name || 'Continental Restaurant',
    lounge: 'Velvet Terrace',
  };

  const fullText = DISPLAY_NAMES[locationKey] || restaurantConfig.name || 'Restaurant';
  const currency = restaurantConfig.currency || '₦';
  const waiterId = parseInt(localStorage.getItem('user_id')) || null;
  
  // ============================================
  // GENERATE TICKET HTML FUNCTION
  // ============================================
  
  const generateTicketHTML = useCallback((ticketData) => {
    const ticketTotal = ticketData.total || ticketData.items.reduce((sum, item) => 
      sum + (item.amount || item.price || 0) * (item.quantity || 1), 0
    );
    
    const currencySymbol = '₦';
    const formatCurrencyLocal = (amount) => {
      return `${currencySymbol} ${Number(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    };
    
    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    };
    
    const formatTime = (timeString) => {
      if (!timeString) return 'N/A';
      const date = new Date(timeString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    };
    
    return `
      <div id="receipt-content">
        <div style="max-width:80mm;margin:0 auto;padding:8px 6px;background:#ffffff;font-family:'Courier New',monospace;font-size:10px;line-height:1.6;color:#000000;font-weight:bold;">
          <div style="text-align:center;padding-bottom:8px;border-bottom:2px solid #000;margin-bottom:8px;">
            <div style="font-size:16px;font-weight:bold;letter-spacing:2px;">${restaurantConfig.name || 'RESTAURANT'}</div>
            ${restaurantConfig.address ? `<div style="font-size:7px;font-weight:bold;">${restaurantConfig.address}</div>` : ''}
            ${restaurantConfig.phone ? `<div style="font-size:7px;font-weight:bold;">Tel: ${restaurantConfig.phone}</div>` : ''}
            <div style="border-top:2px solid #000;margin:6px 0;"></div>
            <div style="font-size:14px;font-weight:bold;letter-spacing:2px;">${ticketData.ticket_title || 'ORDER TICKET'}</div>
            <div style="display:inline-block;padding:4px 16px;border:2px solid #000;font-size:12px;font-weight:bold;letter-spacing:2px;background-color:${ticketData.department === 'Kitchen' ? '#d4edda' : '#cce5ff'};color:${ticketData.department === 'Kitchen' ? '#155724' : '#004085'};margin-top:4px;">
              ${ticketData.department.toUpperCase()} COPY
            </div>
            <div style="display:flex;justify-content:space-between;padding:1px 0;font-size:7px;font-weight:bold;margin-top:4px;">
              <span>${formatDate(ticketData.order_date)}</span>
              <span>${formatTime(ticketData.order_time)}</span>
            </div>
          </div>
          
          <div style="margin-bottom:4px;">
            <div style="display:flex;justify-content:space-between;padding:1px 0;font-size:10px;font-weight:bold;">
              <span>ORDER: ${ticketData.order_number || 'N/A'}</span>
              <span>#${String(ticketData.serial_number || 0).padStart(6, '0')}</span>
            </div>
            
            ${ticketData.order_type === 'DINE_IN' && ticketData.table_name ? `
              <div style="display:flex;justify-content:space-between;padding:1px 0;font-size:9px;font-weight:bold;">
                <span>Table: ${ticketData.table_name}</span>
                <span>DINE IN</span>
              </div>
            ` : ''}
            
            ${ticketData.order_type === 'TAKEAWAY' ? `
              <div style="display:flex;justify-content:space-between;padding:1px 0;font-size:9px;font-weight:bold;">
                <span>TAKEAWAY</span>
              </div>
            ` : ''}
            
            ${ticketData.customer_name ? `
              <div style="display:flex;justify-content:space-between;padding:1px 0;font-size:9px;font-weight:bold;">
                <span>Customer: ${ticketData.customer_name}</span>
              </div>
            ` : ''}
            
            ${ticketData.waiter_name ? `
              <div style="display:flex;justify-content:space-between;padding:1px 0;font-size:9px;font-weight:bold;">
                <span>Waiter: ${ticketData.waiter_name}</span>
              </div>
            ` : ''}
          </div>
          
          <div style="border-top:2px solid #000;margin:6px 0;"></div>
          
          <div style="margin-bottom:4px;">
            <div style="display:flex;justify-content:space-between;padding:4px 2px;border-bottom:2px solid #000;font-weight:bold;font-size:8px;background-color:#f5f5f5;">
              <span style="font-size:8px;font-weight:bold;flex:1;">ITEM</span>
              <span style="font-size:8px;font-weight:bold;min-width:25px;text-align:center;">QTY</span>
              <span style="font-size:8px;font-weight:bold;min-width:60px;text-align:right;">TOTAL</span>
            </div>
            
            ${ticketData.items.map((item, idx) => {
              const quantity = item.quantity || 1;
              const name = item.name || 'Item';
              const price = item.amount || item.price || 0;
              const total = price * quantity;
              const notes = item.notes || '';
              
              return `
                <div style="margin-bottom:2px;" key="${idx}">
                  <div style="display:flex;justify-content:space-between;padding:2px 0;font-size:9.5px;font-weight:bold;border-bottom:1px solid #000;">
                    <span style="font-size:9.5px;font-weight:bold;flex:1;">${name}</span>
                    <span style="font-size:9.5px;font-weight:bold;min-width:25px;text-align:center;">${quantity}</span>
                    <span style="font-size:9.5px;font-weight:bold;min-width:60px;text-align:right;">${formatCurrencyLocal(total)}</span>
                  </div>
                  ${notes ? `
                    <div style="padding-left:8px;margin-bottom:2px;">
                      <span style="font-size:8px;color:#333;font-style:italic;font-weight:bold;">* ${notes}</span>
                    </div>
                  ` : ''}
                </div>
              `;
            }).join('')}
          </div>
          
          <div style="border-top:2px solid #000;margin:6px 0;"></div>
          
          <div style="display:flex;justify-content:space-between;padding:4px 0;font-size:10px;font-weight:bold;">
            <span>TICKET TOTAL</span>
            <span>${formatCurrencyLocal(ticketTotal)}</span>
          </div>
          
          ${ticketData.notes ? `
            <div style="border-top:2px solid #000;margin:6px 0;"></div>
            <div style="margin-bottom:4px;">
              <div style="font-size:7px;color:#666;text-transform:uppercase;font-weight:bold;">Notes</div>
              <div style="font-size:8.5px;color:#333;font-weight:bold;">${ticketData.notes}</div>
            </div>
          ` : ''}
          
          <div style="text-align:center;padding-top:8px;border-top:2px solid #000;margin-top:8px;">
            <div style="font-size:10px;font-weight:bold;">${ticketData.department} Copy</div>
            <div style="font-size:8px;font-weight:bold;">Please prepare for order #${ticketData.order_number}</div>
            <div style="border-top:2px solid #000;margin:6px 0;"></div>
            
            <div style="margin-top:4px;padding-top:2px;display:flex;justify-content:center;gap:2px;flex-wrap:wrap;">
              ${Array(30).fill(0).map(() => `
                <span style="width:2px;height:2px;border-radius:50%;background-color:#999;display:inline-block;"></span>
              `).join('')}
            </div>
            <div style="font-size:5.5px;color:#aaa;margin-top:2px;font-weight:bold;">
              ${restaurantConfig.name || 'Restaurant'} • ${ticketData.department} Copy
            </div>
          </div>
        </div>
      </div>
    `;
  }, []);
  
  // ============================================
  // PRINT SINGLE TICKET FUNCTION
  // ============================================
  
  const printSingleTicket = useCallback((ticketData, ticketType) => {
    try {
      const tempContainer = document.createElement('div');
      tempContainer.id = 'temp-receipt-container';
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      tempContainer.style.width = '80mm';
      tempContainer.style.backgroundColor = 'white';
      
      const ticketHTML = generateTicketHTML(ticketData);
      tempContainer.innerHTML = ticketHTML;
      document.body.appendChild(tempContainer);
      
      const ticketElement = tempContainer.querySelector('#receipt-content');
      
      if (!ticketElement) {
        document.body.removeChild(tempContainer);
        throw new Error('Ticket content not found');
      }
      
      const allStyles = document.querySelectorAll('style');
      let combinedStyles = '';
      allStyles.forEach(style => {
        combinedStyles += style.innerHTML;
      });
      
      const printHTML = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <title>${ticketType} - ${ticketData.order_number}</title>
            <style>
              ${combinedStyles}
              
              @media print {
                * {
                  margin: 0;
                  padding: 0;
                  box-sizing: border-box;
                }
                body {
                  margin: 0;
                  padding: 0;
                  background: white;
                  width: 80mm;
                }
                #receipt-content {
                  max-width: 80mm !important;
                  margin: 0 auto !important;
                  padding: 8px 6px !important;
                  background: white !important;
                  font-family: 'Courier New', monospace !important;
                  font-size: 10px !important;
                }
                .ticket-badge, .status-badge {
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
                .MuiTypography-root {
                  color: black !important;
                }
                @page {
                  size: 80mm auto !important;
                  margin: 0 !important;
                  padding: 0 !important;
                }
              }
              
              body {
                background: #f0f0f0;
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 20px;
              }
              #receipt-content {
                max-width: 80mm;
                margin: 0 auto;
                padding: 10px 8px;
                background: white;
                box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                border-radius: 4px;
              }
              .print-instructions {
                text-align: center;
                padding: 10px;
                margin-bottom: 10px;
                background: #e3f2fd;
                border-radius: 4px;
                font-family: Arial, sans-serif;
                font-size: 12px;
                max-width: 80mm;
              }
            </style>
          </head>
          <body>
            <div class="print-instructions">
              🖨️ Printing ${ticketType} - ${ticketData.department}
              <br>
              <small>Order #${ticketData.order_number}</small>
            </div>
            ${ticketElement.outerHTML}
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  setTimeout(function() {
                    window.close();
                  }, 3000);
                }, 500);
              };
            <\/script>
          </body>
        </html>
      `;

      let printWindow = window.open('', '_blank', 'width=400,height=800,menubar=no,toolbar=no,location=no,status=no,scrollbars=yes');
      
      if (!printWindow || printWindow.closed || typeof printWindow.closed === 'undefined') {
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.left = '-9999px';
        iframe.style.top = '0';
        iframe.style.width = '400px';
        iframe.style.height = '800px';
        document.body.appendChild(iframe);
        
        const iframeDoc = iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(printHTML);
        iframeDoc.close();
        
        setTimeout(() => {
          iframe.contentWindow.print();
          setTimeout(() => {
            document.body.removeChild(iframe);
          }, 5000);
        }, 500);
        
        setTimeout(() => {
          if (document.body.contains(tempContainer)) {
            document.body.removeChild(tempContainer);
          }
        }, 5000);
        
        setSnackbar({
          open: true,
          message: `${ticketType} sent to printer via iframe`,
          severity: 'success',
        });
        
        return;
      }
      
      printWindow.document.write(printHTML);
      printWindow.document.close();
      
      setTimeout(() => {
        if (document.body.contains(tempContainer)) {
          document.body.removeChild(tempContainer);
        }
      }, 5000);
      
      setSnackbar({
        open: true,
        message: `${ticketType} sent to printer!`,
        severity: 'success',
      });
      
    } catch (error) {
      console.error('[MenuPOS] Error printing ticket:', error);
      
      const tempContainer = document.getElementById('temp-receipt-container');
      if (tempContainer && document.body.contains(tempContainer)) {
        document.body.removeChild(tempContainer);
      }
      
      if (error.message.includes('popups')) {
        setSnackbar({
          open: true,
          message: 'Please allow popups for printing. Check your browser settings.',
          severity: 'warning',
        });
      } else {
        setSnackbar({
          open: true,
          message: `Failed to print ${ticketType}: ${error.message}`,
          severity: 'error',
        });
      }
    }
  }, [generateTicketHTML, setSnackbar]);
  
  // ============================================
  // CHECK LOCATION STATUS
  // ============================================
  
  const checkLocationStatus = useCallback(async () => {
    try {
      console.debug(`[MenuPOS] Checking location status for ${LOCATION_NAME}...`);
      const data = await apiRequest(`/eod/location-status/${LOCATION_ID}`);
      console.debug('[MenuPOS] Location status:', data);
      
      if (isMountedRef.current) {
        setLocationStatus({
          can_take_orders: data.can_take_orders,
          is_day_closed: data.is_day_closed,
          has_active_shift: data.has_active_shift,
          status_message: data.status_message,
          loading: false
        });
      }
      
      return data;
    } catch (err) {
      console.error('[MenuPOS] Error checking location status:', err);
      
      if (err.message.includes('403') || err.message.includes('Forbidden') || 
          err.message.includes('401') || err.message.includes('Unauthorized')) {
        if (isMountedRef.current) {
          setLocationStatus({
            can_take_orders: true,
            is_day_closed: false,
            has_active_shift: true,
            status_message: 'Location status could not be verified. Please check with cashier if you experience issues.',
            loading: false
          });
        }
      } else {
        if (isMountedRef.current) {
          setLocationStatus({
            can_take_orders: false,
            is_day_closed: false,
            has_active_shift: false,
            status_message: 'Unable to verify location status. Please try again.',
            loading: false
          });
        }
      }
      return null;
    }
  }, [LOCATION_ID, LOCATION_NAME]);
  
  // ============================================
  // TYPING EFFECT
  // ============================================
  
  useEffect(() => {
    if (isLoading) return;
    
    let index = 0;
    let isDeleting = false;
    
    const typeText = () => {
      if (!isMountedRef.current) return;
      
      if (!isDeleting) {
        if (index <= fullText.length) {
          setDisplayText(fullText.substring(0, index));
          index++;
          typingTimeoutRef.current = setTimeout(typeText, 100);
        } else {
          typingTimeoutRef.current = setTimeout(() => {
            isDeleting = true;
            index = fullText.length;
            typingTimeoutRef.current = setTimeout(typeText, 50);
          }, 3000);
        }
      } else {
        if (index >= 0) {
          setDisplayText(fullText.substring(0, index));
          index--;
          typingTimeoutRef.current = setTimeout(typeText, 50);
        } else {
          isDeleting = false;
          index = 0;
          typingTimeoutRef.current = setTimeout(typeText, 500);
        }
      }
    };
    
    typingTimeoutRef.current = setTimeout(typeText, 500);
    
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      isMountedRef.current = false;
    };
  }, [isLoading, fullText]);
  
  // ============================================
  // DATA FETCHING
  // ============================================
  const fetchCategories = useCallback(async () => {
    try {
      console.debug(`[MenuPOS] Fetching categories for ${LOCATION_NAME}...`);
      const data = await apiRequest(`/categories/location/${LOCATION_NAME}?limit=100`);
      console.debug('[MenuPOS] Categories fetched:', data);
      
      if (isMountedRef.current) {
        const categoriesData = Array.isArray(data) ? data : [];
        setCategories(categoriesData.filter(c => c.is_active === 1 || c.is_active === true));
      }
    } catch (err) {
      console.error('[MenuPOS] Error fetching categories:', err);
      if (isMountedRef.current) {
        setSnackbar({
          open: true,
          message: `Failed to fetch categories: ${err.message}`,
          severity: 'error',
        });
      }
    }
  }, [LOCATION_NAME]);
  
  const fetchMenuItems = useCallback(async () => {
    try {
      console.debug(`[MenuPOS] Fetching menu items for ${LOCATION_NAME}...`);
      setIsFetching(true);
      
      const data = await apiRequest(`/menu-items/location/${LOCATION_NAME}?limit=1000`);
      console.debug('[MenuPOS] Menu items fetched:', data);
      
      if (isMountedRef.current) {
        const itemsData = Array.isArray(data) ? data : [];
        const activeItems = itemsData.filter(item => item.is_active === 1 || item.is_active === true);
        setItems(activeItems);
        setFilteredItems(activeItems);
        console.debug(`[MenuPOS] Loaded ${activeItems.length} active items`);
      }
    } catch (err) {
      console.error('[MenuPOS] Error fetching menu items:', err);
      if (isMountedRef.current) {
        setSnackbar({
          open: true,
          message: `Failed to fetch menu items: ${err.message}`,
          severity: 'error',
        });
      }
    } finally {
      if (isMountedRef.current) {
        setIsFetching(false);
      }
    }
  }, [LOCATION_NAME]);

  const fetchStockStatus = useCallback(async (menuItemIds) => {
    if (!menuItemIds || menuItemIds.length === 0) {
      console.debug('[MenuPOS] No menu items to fetch stock status for');
      // Mark as loaded even if no items
      if (isMountedRef.current) {
        setIsStockStatusLoaded(true);
      }
      return;
    }

    try {
      setFetchingStockStatus(true);
      
      const idsParam = menuItemIds.join(',');
      const endpoint = `/inventory/menu-items/status?location_id=${LOCATION_ID}&menu_item_ids=${idsParam}`;
      
      console.debug(`[MenuPOS] Fetching stock status for ${menuItemIds.length} items...`);
      const response = await apiRequest(endpoint);
      
      if (isMountedRef.current) {
        const statusMap = {};
        if (Array.isArray(response)) {
          response.forEach(item => {
            statusMap[item.menu_item_id] = {
              ...item,
              is_out_of_stock: item.status === 'OUT_OF_STOCK' || item.quantity <= 0,
              is_low_stock: item.status === 'LOW_STOCK' || (item.quantity > 0 && item.quantity <= (item.reorder_point || 0)),
              in_stock: item.is_available === true && item.quantity > 0,
              loading: false,
            };
          });
        } else {
          Object.keys(response || {}).forEach(key => {
            const item = response[key];
            statusMap[key] = {
              ...item,
              is_out_of_stock: item.status === 'OUT_OF_STOCK' || item.quantity <= 0,
              is_low_stock: item.status === 'LOW_STOCK' || (item.quantity > 0 && item.quantity <= (item.reorder_point || 0)),
              in_stock: item.is_available === true && item.quantity > 0,
              loading: false,
            };
          });
        }
        
        setStockStatusMap(statusMap);
        // CRITICAL: Mark stock status as loaded
        setIsStockStatusLoaded(true);
        
        const trackedCount = Object.values(statusMap).filter(
          status => status.status !== 'NOT_TRACKED'
        ).length;
        console.debug(`[MenuPOS] Stock status loaded: ${trackedCount} tracked items`, statusMap);
      }
    } catch (err) {
      console.error('[MenuPOS] Error fetching stock status:', err);
      if (isMountedRef.current) {
        // Even on error, mark as loaded to prevent infinite loading state
        setIsStockStatusLoaded(true);
        setStockStatusMap({});
      }
    } finally {
      if (isMountedRef.current) {
        setFetchingStockStatus(false);
      }
    }
  }, [LOCATION_ID]);


  // ============================================
// REAL-TIME STOCK REFRESH
// ============================================

const refreshStockStatus = useCallback(async () => {
  if (!items.length) return;
  
  try {
    // Get all item IDs in cart
    const cartItemIds = cartItems.map(item => item.id);
    
    // If cart is empty, no need to refresh
    if (cartItemIds.length === 0) return;
    
    console.debug('[MenuPOS] Refreshing stock status for cart items...');
    
    const idsParam = cartItemIds.join(',');
    const endpoint = `/inventory/menu-items/status?location_id=${LOCATION_ID}&menu_item_ids=${idsParam}`;
    
    const response = await apiRequest(endpoint);
    
    if (isMountedRef.current && response) {
      const statusMap = { ...stockStatusMap };
      
      if (Array.isArray(response)) {
        response.forEach(item => {
          statusMap[item.menu_item_id] = {
            ...item,
            is_out_of_stock: item.status === 'OUT_OF_STOCK' || item.quantity <= 0,
            is_low_stock: item.status === 'LOW_STOCK' || (item.quantity > 0 && item.quantity <= (item.reorder_point || 0)),
            in_stock: item.is_available === true && item.quantity > 0,
            loading: false,
          };
        });
      }
      
      setStockStatusMap(statusMap);
      
      // Check if any cart items are now out of stock
      const nowOutOfStock = cartItems.filter(item => {
        const status = statusMap[item.id];
        return status && (status.is_out_of_stock || status.status === 'OUT_OF_STOCK');
      });
      
      if (nowOutOfStock.length > 0) {
        setSnackbar({
          open: true,
          message: `⚠️ ${nowOutOfStock.length} item(s) in your cart are now out of stock! Please remove them.`,
          severity: 'warning',
        });
      }
    }
  } catch (err) {
    console.error('[MenuPOS] Error refreshing stock status:', err);
  }
}, [cartItems, items, LOCATION_ID, stockStatusMap]);

// Add this useEffect to refresh stock when cart changes
useEffect(() => {
  // Debounce stock refresh - don't refresh too frequently
  const timeoutId = setTimeout(() => {
    if (cartItems.length > 0) {
      refreshStockStatus();
    }
  }, 1000); // Wait 1 second after cart changes before refreshing
  
  return () => clearTimeout(timeoutId);
}, [cartItems, refreshStockStatus]);


  const fetchTables = useCallback(async () => {
    try {
      console.debug('[MenuPOS] Fetching available tables...');
      setLoadingTables(true);
      
      const data = await apiRequest(`/tables/available?location_id=${LOCATION_ID}`);
      console.debug('[MenuPOS] Tables fetched:', data);
      
      if (isMountedRef.current) {
        setTables(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('[MenuPOS] Error fetching tables:', err);
      setTables([]);
    } finally {
      if (isMountedRef.current) {
        setLoadingTables(false);
      }
    }
  }, [LOCATION_ID]);
  
  // ============================================
  // CHECK FOR OUT-OF-STOCK ITEMS IN CART
  // ============================================
  
  
  useEffect(() => {
    const hasOutOfStock = cartItems.some(item => {
      const stockInfo = stockStatusMap[item.id];
      return stockInfo && (stockInfo.is_out_of_stock || stockInfo.status === 'OUT_OF_STOCK');
    });
    setHasOutOfStockInCart(hasOutOfStock);
  }, [cartItems, stockStatusMap]);
  
  // ============================================
  // INITIALIZATION
  // ============================================

  useEffect(() => {
    isMountedRef.current = true;
    
    console.debug(`[MenuPOS] Initializing for ${LOCATION_NAME}...`);
    
    // Fetch all data including location status
    Promise.all([
      fetchCategories(), 
      fetchMenuItems(),
      checkLocationStatus()
    ]);
    
    statusCheckIntervalRef.current = setInterval(() => {
      if (isMountedRef.current) {
        console.debug('[MenuPOS] Periodic location status check...');
        checkLocationStatus();
      }
    }, 60000);
    
    const timer = setTimeout(() => {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }, 1500);
    
    return () => {
      clearTimeout(timer);
      if (statusCheckIntervalRef.current) {
        clearInterval(statusCheckIntervalRef.current);
      }
      isMountedRef.current = false;
    };
  }, [fetchCategories, fetchMenuItems, checkLocationStatus, LOCATION_NAME]);

  // Add this to the initialization useEffect or create a new one

useEffect(() => {
  // Periodic stock refresh every 30 seconds for cart items
  const stockRefreshInterval = setInterval(() => {
    if (cartItems.length > 0 && isMountedRef.current) {
      console.debug('[MenuPOS] Periodic stock refresh...');
      refreshStockStatus();
    }
  }, 30000); // Refresh every 30 seconds
  
  return () => clearInterval(stockRefreshInterval);
}, [cartItems, refreshStockStatus]);

  // Fetch stock status after menu items load
  useEffect(() => {
    if (items.length > 0) {
      const menuItemIds = items.map(item => item.id);
      fetchStockStatus(menuItemIds);
    } else {
      // If no items, mark stock status as loaded
      setIsStockStatusLoaded(true);
    }
  }, [items, fetchStockStatus]);
  
  // ============================================
  // TOKEN AUTO-REFRESH
  // ============================================
  
  useEffect(() => {
    const checkAndRefreshToken = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      
      const expiryTime = getTokenExpiryTime(token);
      const timeLeft = expiryTime - Date.now();
      
      if (timeLeft > 0 && timeLeft < 120000) {
        console.debug('[MenuPOS] Auto-refreshing token...');
        await refreshAuthToken();
      }
    };
    
    const refreshInterval = setInterval(checkAndRefreshToken, 60000);
    return () => clearInterval(refreshInterval);
  }, []);
  
  // ============================================
  // FILTERING & SEARCH
  // ============================================
  
  const applyFilters = useCallback(() => {
    let filtered = [...items];
    
    if (selectedCategory !== 'ALL') {
      filtered = filtered.filter(item => {
        const itemCategory = item.category?.toLowerCase() || '';
        const selectedCat = selectedCategory.toLowerCase();
        return itemCategory === selectedCat;
      });
    }
    
    if (searchTerm && searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(item => {
        const name = item.name?.toLowerCase() || '';
        const category = item.category?.toLowerCase() || '';
        return name.includes(term) || category.includes(term);
      });
    }
    
    filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    
    setFilteredItems(filtered);
    setPage(1);
  }, [items, selectedCategory, searchTerm]);
  
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      applyFilters();
    }, 300);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [applyFilters, searchTerm]);
  
  // ============================================
  // CART MANAGEMENT
  // ============================================
  
 const addToCart = useCallback((item) => {
  // Check if location is open before adding to cart
  if (!locationStatus.can_take_orders) {
    setSnackbar({
      open: true,
      message: locationStatus.status_message || 'This location is currently closed. Please contact your cashier.',
      severity: 'error',
    });
    return;
  }
  
  if (!item || !item.id) {
    console.error('[MenuPOS] Invalid item for cart:', item);
    setSnackbar({
      open: true,
      message: 'Invalid item data',
      severity: 'error',
    });
    return;
  }
  
  if (item.is_active !== 1 && item.is_active !== true) {
    setSnackbar({
      open: true,
      message: `${item.name} is currently unavailable`,
      severity: 'warning',
    });
    return;
  }
  
  // CRITICAL: Check if stock status has loaded
  if (!isStockStatusLoaded) {
    setSnackbar({
      open: true,
      message: 'Stock information is still loading. Please wait...',
      severity: 'info',
    });
    return;
  }
  
  // Check stock status
  const stockInfo = stockStatusMap[item.id];
  if (stockInfo) {
    // Check if item is out of stock
    if (stockInfo.is_out_of_stock || stockInfo.status === 'OUT_OF_STOCK') {
      setSnackbar({
        open: true,
        message: `${item.name} is out of stock!`,
        severity: 'error',
      });
      return;
    }
    
    // Check if adding more than available
    if (stockInfo.quantity !== null && stockInfo.in_stock) {
      // Get current quantity in cart for this item
      const currentCartItem = cartItems.find(c => c.id === item.id);
      const currentQuantity = currentCartItem?.quantity || 0;
      const newTotal = currentQuantity + 1;
      
      // Check if new total exceeds available stock
      if (newTotal > stockInfo.quantity) {
        setSnackbar({
          open: true,
          message: `Only ${stockInfo.quantity} ${stockInfo.unit || 'unit'}${stockInfo.quantity > 1 ? 's' : ''} of ${item.name} available. You already have ${currentQuantity} in cart.`,
          severity: 'warning',
        });
        return;
      }
    }
  }
  
  setIsAddingToCart(true);
  
  try {
    setCartItems(prevCart => {
      const existingIndex = prevCart.findIndex(cartItem => cartItem.id === item.id);
      
      if (existingIndex >= 0) {
        const newCart = [...prevCart];
        // Double-check stock again before adding
        const stockInfoCheck = stockStatusMap[item.id];
        const newQuantity = newCart[existingIndex].quantity + 1;
        
        if (stockInfoCheck && stockInfoCheck.quantity !== null && newQuantity > stockInfoCheck.quantity) {
          // This should not happen if the check above works, but just in case
          setSnackbar({
            open: true,
            message: `Cannot add more ${item.name}. Only ${stockInfoCheck.quantity} available.`,
            severity: 'warning',
          });
          return prevCart;
        }
        
        newCart[existingIndex] = {
          ...newCart[existingIndex],
          quantity: newQuantity,
        };
        return newCart;
      }
      
      return [...prevCart, {
        id: item.id,
        name: item.name || 'Unknown Item',
        amount: parseFloat(item.amount) || 0,
        category: item.category || 'Uncategorized',
        main_category: item.main_category || 'Food',
        quantity: 1,
      }];
    });
    
  } catch (error) {
    console.error('[MenuPOS] Error adding to cart:', error);
    setSnackbar({
      open: true,
      message: 'Failed to add item to cart',
      severity: 'error',
    });
  } finally {
    setIsAddingToCart(false);
  }
}, [locationStatus, stockStatusMap, cartItems, isStockStatusLoaded]);
  
  const removeFromCart = useCallback((itemId) => {
    setCartItems(prevCart => prevCart.filter(item => item.id !== itemId));
  }, []);
  
 // ============================================
// CART MANAGEMENT - FIXED
// ============================================

const updateQuantity = useCallback((itemId, quantity) => {
  if (quantity <= 0) {
    removeFromCart(itemId);
    return;
  }
  
  // Get the item from cart
  const cartItem = cartItems.find(c => c.id === itemId);
  if (!cartItem) return;
  
  // Get stock info for this item
  const stockInfo = stockStatusMap[itemId];
  
  // If item tracks inventory, validate stock availability
  if (stockInfo && stockInfo.status !== 'NOT_TRACKED' && stockInfo.quantity !== null) {
    // Calculate TOTAL quantity in cart for this item (including the change)
    const totalAfterChange = quantity;
    
    // Check if total quantity exceeds available stock
    if (totalAfterChange > stockInfo.quantity) {
      setSnackbar({
        open: true,
        message: `Only ${stockInfo.quantity} ${stockInfo.unit || 'unit'}${stockInfo.quantity > 1 ? 's' : ''} of ${cartItem.name} available. You already have ${cartItem.quantity} in cart.`,
        severity: 'warning',
      });
      return;
    }
  }
  
  setCartItems(prevCart =>
    prevCart.map(item =>
      item.id === itemId ? { ...item, quantity } : item
    )
  );
}, [removeFromCart, cartItems, stockStatusMap]);
  
  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);
  
  const getCartTotal = useCallback(() => {
    return cartItems.reduce((total, item) => total + (item.amount || 0) * item.quantity, 0);
  }, [cartItems]);
  
  const getCartItemCount = useCallback(() => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  }, [cartItems]);
  
  // ============================================
  // INVENTORY CHECK BEFORE SUBMIT
  // ============================================
  
  const checkInventoryBeforeSubmit = useCallback(async () => {
    try {
      const checkItems = cartItems.map(item => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        serving_size: 1.0,
        track_inventory: true
      }));

      console.debug('[MenuPOS] Checking inventory before submit:', checkItems);

      const response = await apiRequest('/orders/check-inventory', {
        method: 'POST',
        body: JSON.stringify({
          items: checkItems,
          location_id: LOCATION_ID
        }),
      });

      console.debug('[MenuPOS] Inventory check response:', response);

      if (!response.all_available) {
        let errorMessage = '❌ Cannot place order - Some items have stock issues:\n';
        
        response.items.forEach(item => {
          if (!item.is_available && item.status !== 'NOT_TRACKED') {
            errorMessage += `\n• ${item.name}: ${item.message}`;
            if (item.available_quantity !== undefined) {
              errorMessage += ` (Available: ${item.available_quantity})`;
            }
          }
        });

        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
        
        // Update stock status to reflect current state
        if (response.items) {
          const updatedStockMap = { ...stockStatusMap };
          response.items.forEach(item => {
            if (item.menu_item_id) {
              updatedStockMap[item.menu_item_id] = {
                ...updatedStockMap[item.menu_item_id],
                quantity: item.available_quantity || 0,
                is_out_of_stock: !item.is_available,
                is_low_stock: item.is_available && item.available_quantity <= 5,
                in_stock: item.is_available && item.available_quantity > 0,
                status: item.status || 'OUT_OF_STOCK',
              };
            }
          });
          setStockStatusMap(updatedStockMap);
        }
        
        return false;
      }

      return true;
    } catch (error) {
      console.error('[MenuPOS] Error checking inventory:', error);
      setSnackbar({
        open: true,
        message: 'Failed to verify inventory: ' + error.message,
        severity: 'error',
      });
      return false;
    }
  }, [cartItems, LOCATION_ID, stockStatusMap]);
  
  // ============================================
  // ORDER HANDLING
  // ============================================
  
  const handleOpenOrderDialog = useCallback(() => {
    if (cartItems.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please add items to your cart first',
        severity: 'warning',
      });
      return;
    }
    
    // Check if stock status has loaded
    if (!isStockStatusLoaded) {
      setSnackbar({
        open: true,
        message: 'Stock information is still loading. Please wait...',
        severity: 'info',
      });
      return;
    }
    
    // Check if there are out-of-stock items in cart
    if (hasOutOfStockInCart) {
      setSnackbar({
        open: true,
        message: '⚠️ Your cart contains out-of-stock items. Please remove them before placing an order.',
        severity: 'error',
      });
      return;
    }
    
    if (!locationStatus.can_take_orders) {
      setSnackbar({
        open: true,
        message: locationStatus.status_message || 'This location is currently closed. Please contact your cashier.',
        severity: 'error',
      });
      return;
    }
    
    setOrderType('DINE_IN');
    setSelectedTable(null);
    setCustomerPhone('');
    setOrderNotes('');
    setOrderDialogOpen(true);
    
    fetchTables();
  }, [cartItems, fetchTables, locationStatus, hasOutOfStockInCart, isStockStatusLoaded]);
  
  const handleCloseOrderDialog = useCallback(() => {
    setOrderDialogOpen(false);
    setSelectedTable(null);
    setCustomerPhone('');
    setOrderNotes('');
  }, []);
  
  // ============================================
  // HANDLE SUBMIT ORDER WITH INVENTORY CHECK
  // ============================================
  
  const handleSubmitOrder = useCallback(async () => {
    if (orderType === 'DINE_IN' && !selectedTable) {
      setSnackbar({
        open: true,
        message: 'Please select a table for dine-in',
        severity: 'warning',
      });
      return;
    }
    
    // Check if stock status has loaded
    if (!isStockStatusLoaded) {
      setSnackbar({
        open: true,
        message: 'Stock information is still loading. Please wait...',
        severity: 'info',
      });
      return;
    }
    
    // Check if there are out-of-stock items in cart
    if (hasOutOfStockInCart) {
      setSnackbar({
        open: true,
        message: '⚠️ Your cart contains out-of-stock items. Please remove them before placing an order.',
        severity: 'error',
      });
      return;
    }
    
    setIsSubmittingOrder(true);
    
    try {
      // Run inventory check before submitting
      const inventoryOk = await checkInventoryBeforeSubmit();
      if (!inventoryOk) {
        setIsSubmittingOrder(false);
        return;
      }
      
      const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
      const totalAmount = cartItems.reduce((sum, item) => sum + (item.amount * item.quantity), 0);
      
      setOrderTotalItems(totalItems);
      setOrderTotalAmount(totalAmount);
      
      const orderItems = cartItems.map(item => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        price: item.amount,
        total: item.amount * item.quantity,
        category: item.category,
        main_category: item.main_category,
        notes: null,
      }));
      
      const orderData = {
        location_id: LOCATION_ID,
        order_type: orderType,
        table_id: orderType === 'DINE_IN' ? selectedTable : null,
        waiter_id: waiterId,
        customer_name: orderType === 'DINE_IN' ? null : 'Takeaway Customer',
        customer_phone: orderType === 'TAKEAWAY' ? customerPhone : null,
        items: orderItems,
        notes: orderNotes || null,
        tax: 0,
        discount: 0,
        order_date: new Date().toISOString().split('T')[0],
      };
      
      console.debug('[MenuPOS] Submitting order:', orderData);
      
      const response = await apiRequest('/orders/', {
        method: 'POST',
        body: JSON.stringify(orderData),
      });
      
      console.debug('[MenuPOS] Order response:', response);
      
      const foodItems = cartItems.filter(item => item.main_category === 'Food');
      const drinkItems = cartItems.filter(item => item.main_category === 'Drinks');
      const hasFood = foodItems.length > 0;
      const hasDrinks = drinkItems.length > 0;
      
      const baseTicketData = {
        order_number: response.order_number,
        serial_number: response.serial_number,
        order_type: orderType,
        table_name: orderType === 'DINE_IN' ? tables.find(t => t.id === selectedTable)?.table_name : null,
        waiter_name: 'Waiter',
        order_date: new Date().toISOString(),
        order_time: new Date().toISOString(),
        notes: orderNotes || null,
        customer_name: orderType === 'DINE_IN' ? null : 'Takeaway Customer',
        customer_phone: orderType === 'TAKEAWAY' ? customerPhone : null,
      };
      
      if (hasFood) {
        setKotTicketData({
          ...baseTicketData,
          items: foodItems,
          ticket_type: 'KOT',
          ticket_title: 'KITCHEN ORDER TICKET',
          department: 'Kitchen',
          total: foodItems.reduce((sum, item) => sum + (item.amount * item.quantity), 0),
        });
      } else {
        setKotTicketData(null);
      }
      
      if (hasDrinks) {
        setBotTicketData({
          ...baseTicketData,
          items: drinkItems,
          ticket_type: 'BOT',
          ticket_title: 'BAR ORDER TICKET',
          department: 'Bar',
          total: drinkItems.reduce((sum, item) => sum + (item.amount * item.quantity), 0),
        });
      } else {
        setBotTicketData(null);
      }
      
      setReceiptData(response);
      setReceiptDialogOpen(true);
      setPrintProgress(0);
      
      setSnackbar({
        open: true,
        message: `Order #${response.order_number} created successfully! Select tickets to print.`,
        severity: 'success',
      });
      
      clearCart();
      handleCloseOrderDialog();
      
    } catch (error) {
      console.error('[MenuPOS] Error submitting order:', error);
      
      // Parse error message from backend
      let errorMsg = error.message;
      if (errorMsg.includes('Insufficient stock') || errorMsg.includes('out of stock')) {
        errorMsg = '❌ ' + errorMsg;
        const itemNames = errorMsg.match(/'(.*?)'/g);
        if (itemNames) {
          errorMsg = '❌ Stock issue: ' + errorMsg;
        }
      }
      
      setSnackbar({
        open: true,
        message: errorMsg,
        severity: 'error',
      });
    } finally {
      setIsSubmittingOrder(false);
    }
  }, [cartItems, orderType, selectedTable, customerPhone, orderNotes, waiterId, clearCart, handleCloseOrderDialog, tables, LOCATION_ID, hasOutOfStockInCart, checkInventoryBeforeSubmit, isStockStatusLoaded]);
  
  // ============================================
  // PRINT HANDLING
  // ============================================
  
  const handlePrintKOT = useCallback(() => {
    if (!kotTicketData) {
      setSnackbar({
        open: true,
        message: 'No Kitchen Order Ticket to print',
        severity: 'warning',
      });
      return;
    }
    
    try {
      setIsPrinting(true);
      setPrintProgress(50);
      printSingleTicket(kotTicketData, 'KOT');
      setPrintProgress(100);
      setTimeout(() => {
        setIsPrinting(false);
        setPrintProgress(0);
      }, 2000);
    } catch (error) {
      console.error('[MenuPOS] Error printing KOT:', error);
      setSnackbar({
        open: true,
        message: 'Failed to print KOT: ' + error.message,
        severity: 'error',
      });
      setIsPrinting(false);
      setPrintProgress(0);
    }
  }, [kotTicketData, printSingleTicket]);
  
  const handlePrintBOT = useCallback(() => {
    if (!botTicketData) {
      setSnackbar({
        open: true,
        message: 'No Bar Order Ticket to print',
        severity: 'warning',
      });
      return;
    }
    
    try {
      setIsPrinting(true);
      setPrintProgress(50);
      printSingleTicket(botTicketData, 'BOT');
      setPrintProgress(100);
      setTimeout(() => {
        setIsPrinting(false);
        setPrintProgress(0);
      }, 2000);
    } catch (error) {
      console.error('[MenuPOS] Error printing BOT:', error);
      setSnackbar({
        open: true,
        message: 'Failed to print BOT: ' + error.message,
        severity: 'error',
      });
      setIsPrinting(false);
      setPrintProgress(0);
    }
  }, [botTicketData, printSingleTicket]);
  
  const handlePrintReceipt = useCallback(() => {
    try {
      setIsPrinting(true);
      setPrintProgress(30);

      if (!kotTicketData && !botTicketData) {
        setSnackbar({
          open: true,
          message: 'No tickets available to print',
          severity: 'warning',
        });
        setIsPrinting(false);
        setPrintProgress(0);
        return;
      }

      let ticketsHTML = '';
      let ticketCount = 0;

      if (kotTicketData) {
        ticketCount++;
        const kotHTML = generateTicketHTML(kotTicketData);
        ticketsHTML += kotHTML;
        
        if (botTicketData) {
          ticketsHTML += `
            <div style="
              border-top: 3px dashed #999;
              margin: 10px 0;
              padding: 5px 0;
              text-align: center;
            ">
              <span style="font-size: 8px; color: #999; font-weight: bold;">--- CUT HERE ---</span>
            </div>
          `;
        }
      }

      if (botTicketData) {
        ticketCount++;
        const botHTML = generateTicketHTML(botTicketData);
        ticketsHTML += botHTML;
      }

      if (!ticketsHTML) {
        setSnackbar({
          open: true,
          message: 'No ticket content to print',
          severity: 'error',
        });
        setIsPrinting(false);
        setPrintProgress(0);
        return;
      }

      const allStyles = document.querySelectorAll('style');
      let combinedStyles = '';
      allStyles.forEach(style => {
        combinedStyles += style.innerHTML;
      });

      const printWindow = window.open('', '_blank', 'width=400,height=800,menubar=no,toolbar=no,location=no,status=no,scrollbars=yes');
      if (!printWindow) {
        setSnackbar({
          open: true,
          message: 'Please allow popups for printing',
          severity: 'warning',
        });
        setIsPrinting(false);
        setPrintProgress(0);
        return;
      }

      const printHTML = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Receipt - ${receiptData?.order_number || 'Order'}</title>
            <style>
              ${combinedStyles}
              
              @media print {
                * {
                  margin: 0;
                  padding: 0;
                  box-sizing: border-box;
                }
                
                body {
                  margin: 0;
                  padding: 0;
                  background: white;
                  width: 80mm;
                }
                
                #receipt-content {
                  max-width: 80mm !important;
                  margin: 0 auto !important;
                  padding: 8px 6px !important;
                  background: white !important;
                  font-family: 'Courier New', monospace !important;
                  font-size: 10px !important;
                }
                
                .ticket-badge, .status-badge, .MuiChip-root {
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
                
                .MuiTypography-root {
                  color: black !important;
                }
                
                @page {
                  size: 80mm auto !important;
                  margin: 0 !important;
                  padding: 0 !important;
                }
              }
              
              body {
                background: #f0f0f0;
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 20px;
              }
              
              #receipt-content {
                max-width: 80mm;
                margin: 0 auto;
                padding: 10px 8px;
                background: white;
                box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                border-radius: 4px;
              }

              .print-header {
                text-align: center;
                padding: 10px;
                margin-bottom: 10px;
                background: #e3f2fd;
                border-radius: 4px;
                font-family: Arial, sans-serif;
                font-size: 12px;
                max-width: 80mm;
              }
            </style>
          </head>
          <body>
            <div class="print-header">
              🖨️ Printing ${ticketCount} ticket${ticketCount > 1 ? 's' : ''}
              <br>
              <small>Order #${receiptData?.order_number || 'N/A'}</small>
            </div>
            ${ticketsHTML}
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  setTimeout(function() {
                    window.close();
                  }, 3000);
                }, 800);
              };
            <\/script>
          </body>
        </html>
      `;

      printWindow.document.write(printHTML);
      printWindow.document.close();
      setPrintProgress(100);

      setSnackbar({
        open: true,
        message: `${ticketCount} ticket${ticketCount > 1 ? 's' : ''} sent to printer!`,
        severity: 'success',
      });

    } catch (error) {
      console.error('[MenuPOS] Error printing receipt:', error);
      setSnackbar({
        open: true,
        message: 'Failed to print receipt: ' + error.message,
        severity: 'error',
      });
    } finally {
      setTimeout(() => {
        setIsPrinting(false);
        setPrintProgress(0);
      }, 2000);
    }
  }, [kotTicketData, botTicketData, receiptData, generateTicketHTML]);
  
  // ============================================
  // RENDER HELPERS
  // ============================================
  
  const getMainCategoryIcon = useCallback((mainCategory) => {
    if (mainCategory === 'Food') {
      return <RestaurantIcon fontSize="small" sx={{ color: ACCENT_COLOR }} />;
    } else if (mainCategory === 'Drinks') {
      return <DrinkIcon fontSize="small" sx={{ color: '#1976D2' }} />;
    }
    return <FoodIcon fontSize="small" sx={{ color: LOCATION_COLOR }} />;
  }, [ACCENT_COLOR, LOCATION_COLOR]);
  
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);
  
  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);
  
  const handleCategoryChange = useCallback((category) => {
    setSelectedCategory(category);
  }, []);
  
  const handlePageChange = useCallback((_, value) => {
    setPage(value);
    const itemsContainer = document.getElementById('items-container');
    if (itemsContainer) {
      itemsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);
  
  const handlePlaceOrder = useCallback(() => {
    handleOpenOrderDialog();
  }, [handleOpenOrderDialog]);
  
  const handleCloseReceiptDialog = useCallback(() => {
    setReceiptDialogOpen(false);
    setIsPrinting(false);
    setPrintProgress(0);
    setOrderTotalItems(0);
    setOrderTotalAmount(0);
  }, []);
  
  // ============================================
  // LOADING STATE
  // ============================================
  
  if (isLoading) {
    return <Preloader text={`Loading ${LOCATION_NAME} Menu...`} size={70} fullScreen={true} />;
  }
  
  // ============================================
  // MAIN RENDER
  // ============================================
  
  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);
  const paginatedItems = filteredItems.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );
  
  const cartWidth = isSmallMobile ? 220 : isMobile ? 280 : 320;
  const mainContentFlex = isMobile ? `1 1 calc(100% - ${cartWidth}px)` : 1;
  const itemCardHeight = isSmallMobile ? 160 : isMobile ? 170 : CARD_HEIGHT;
  
  let Sidebar;
  try {
    Sidebar = require(`../../../components/RestaurantPOS/waiters/${locationKey}/Sidebar`).default;
  } catch (e) {
    Sidebar = require('../../../components/RestaurantPOS/waiters/african/Sidebar').default;
  }
  
  return (
    <>
      <div className="epos-wrapper" style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <div className="epos-main-layout" style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <Sidebar activeMenu={activeMenu} setActiveMenu={setActiveMenu} />
          
          <div className="epos-main" style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'relative',
          }}>
            <Header />
            
            <Box sx={{
              display: 'flex',
              flex: 1,
              overflow: 'hidden',
              position: 'relative',
            }}>
              {/* Main Content */}
              <Box sx={{
                flex: mainContentFlex,
                overflow: 'auto',
                p: { xs: 1, sm: 3 },
                '&::-webkit-scrollbar': {
                  width: 6,
                },
                '&::-webkit-scrollbar-track': {
                  bgcolor: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: 'grey.300',
                  borderRadius: 3,
                },
              }}>
                {/* Hero Section */}
                <Box sx={{
                  bgcolor: 'white',
                  borderRadius: '16px',
                  p: { xs: 1.5, sm: 3 },
                  mb: { xs: 2, sm: 3 },
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  flexWrap: 'wrap',
                  gap: 1.5,
                  border: `1px solid ${alpha(LOCATION_COLOR, 0.15)}`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                }}>
                  <Box>
                    <Typography variant="h4" sx={{
                      color: LOCATION_COLOR,
                      fontWeight: 'bold',
                      fontSize: { xs: '1rem', sm: '2rem', md: '2.125rem' },
                    }}>
                      {displayText}
                      <span style={{
                        display: 'inline-block',
                        width: '3px',
                        height: { xs: '20px', sm: '28px' },
                        backgroundColor: LOCATION_COLOR,
                        marginLeft: '4px',
                        verticalAlign: 'text-bottom',
                        animation: 'blink 1s step-end infinite',
                      }} />
                    </Typography>
                  </Box>
                  
                  <Badge badgeContent={getCartItemCount()} color="error" max={99}>
                    <Button
                      variant="contained"
                      sx={{
                        bgcolor: LOCATION_COLOR,
                        '&:hover': { bgcolor: LOCATION_COLOR, opacity: 0.9 },
                        borderRadius: '12px',
                        px: { xs: 1.5, sm: 3 },
                        fontWeight: 600,
                        width: { xs: '100%', sm: 'auto' },
                        fontSize: { xs: '0.65rem', sm: '0.875rem' },
                        py: { xs: 0.5, sm: 1 },
                      }}
                      startIcon={<CartIcon sx={{ fontSize: { xs: '0.8rem', sm: '1.2rem' } }} />}
                    >
                      <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.875rem' } }}>
                        {formatCurrency(getCartTotal())}
                      </Typography>
                    </Button>
                  </Badge>
                </Box>

                {/* Location Status Banner */}
                {!locationStatus.loading && !locationStatus.can_take_orders && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mb: 2, 
                      borderRadius: '12px',
                      '& .MuiAlert-icon': { fontSize: '1.5rem' }
                    }}
                    icon={<LockClockIcon />}
                  >
                    <Typography variant="body1" fontWeight={600}>
                      ⛔ {locationStatus.status_message || 'This location is currently closed. Please contact your cashier.'}
                    </Typography>
                  </Alert>
                )}
                
                {/* Stock Status Loading Banner */}
                {!isStockStatusLoaded && (
                  <Alert 
                    severity="info" 
                    sx={{ 
                      mb: 2, 
                      borderRadius: '12px',
                      '& .MuiAlert-icon': { fontSize: '1.5rem' }
                    }}
                    icon={<CircularProgress size={20} />}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      Loading stock information... Please wait before adding items to cart.
                    </Typography>
                  </Alert>
                )}
                
                {/* Out of Stock Warning in Cart Banner */}
                {hasOutOfStockInCart && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mb: 2, 
                      borderRadius: '12px',
                      '& .MuiAlert-icon': { fontSize: '1.5rem' }
                    }}
                    action={
                      <Button 
                        color="error" 
                        size="small" 
                        onClick={() => {
                          const outOfStockItems = cartItems.filter(item => {
                            const stockInfo = stockStatusMap[item.id];
                            return stockInfo && (stockInfo.is_out_of_stock || stockInfo.status === 'OUT_OF_STOCK');
                          });
                          outOfStockItems.forEach(item => removeFromCart(item.id));
                          setSnackbar({
                            open: true,
                            message: `Removed ${outOfStockItems.length} out-of-stock item(s) from cart`,
                            severity: 'success',
                          });
                        }}
                        sx={{ fontSize: '0.7rem', textTransform: 'none', fontWeight: 600 }}
                      >
                        Remove All
                      </Button>
                    }
                  >
                    <Typography variant="body2" fontWeight={600}>
                      ⚠️ Your cart contains out-of-stock items. Please remove them before placing an order.
                    </Typography>
                  </Alert>
                )}
                
                {/* Search Bar */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, mb: { xs: 2, sm: 3 }, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    placeholder={`Search ${LOCATION_NAME} menu items...`}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    sx={{
                      flex: 1,
                      minWidth: '120px',
                      maxWidth: { xs: '100%', sm: '400px' },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        bgcolor: 'white',
                        '& fieldset': {
                          borderColor: alpha(LOCATION_COLOR, 0.2),
                        },
                        '&:hover fieldset': {
                          borderColor: LOCATION_COLOR,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: LOCATION_COLOR,
                        },
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: '#94a3b8', fontSize: { xs: '0.8rem', sm: '1.2rem' } }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={clearSearch}>
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: { xs: '0.55rem', sm: '0.75rem' } }}>
                    {filteredItems.length} items
                  </Typography>
                </Box>
                
                {/* Category Tabs */}
                <Box sx={{ mb: { xs: 2, sm: 4 } }}>
                  <Box sx={{
                    display: 'flex',
                    gap: { xs: 0.75, sm: 1.5 },
                    overflowX: 'auto',
                    pb: 1,
                    '&::-webkit-scrollbar': {
                      height: 4,
                    },
                    '&::-webkit-scrollbar-track': {
                      bgcolor: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      bgcolor: 'grey.300',
                      borderRadius: 4,
                    },
                  }}>
                    <Button
                      onClick={() => handleCategoryChange('ALL')}
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: '0.55rem', sm: '0.85rem' },
                        px: { xs: 1.5, sm: 3 },
                        py: { xs: 0.75, sm: 1.5 },
                        minHeight: { xs: '30px', sm: '48px' },
                        minWidth: { xs: '45px', sm: '80px' },
                        borderRadius: '8px',
                        bgcolor: selectedCategory === 'ALL' ? LOCATION_COLOR : 'white',
                        color: selectedCategory === 'ALL' ? 'white' : 'text.primary',
                        border: selectedCategory === 'ALL' ? 'none' : `1px solid ${alpha('#000', 0.08)}`,
                        '&:hover': {
                          bgcolor: selectedCategory === 'ALL' ? LOCATION_COLOR : alpha(LOCATION_COLOR, 0.05),
                        },
                        textTransform: 'none',
                        flexShrink: 0,
                        cursor: 'pointer',
                      }}
                    >
                      ALL
                    </Button>
                    
                    {categories.map((category) => {
                      const itemCount = items.filter(item =>
                        item.category?.toLowerCase() === category.name.toLowerCase()
                      ).length;
                      
                      return (
                        <Button
                          key={category.id}
                          onClick={() => handleCategoryChange(category.name)}
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: '0.55rem', sm: '0.85rem' },
                            px: { xs: 1.5, sm: 3 },
                            py: { xs: 0.75, sm: 1.5 },
                            minHeight: { xs: '30px', sm: '48px' },
                            minWidth: { xs: '50px', sm: '100px' },
                            borderRadius: '8px',
                            bgcolor: selectedCategory === category.name ? LOCATION_COLOR : 'white',
                            color: selectedCategory === category.name ? 'white' : 'text.primary',
                            border: selectedCategory === category.name ? 'none' : `1px solid ${alpha('#000', 0.08)}`,
                            '&:hover': {
                              bgcolor: selectedCategory === category.name ? LOCATION_COLOR : alpha(LOCATION_COLOR, 0.05),
                            },
                            textTransform: 'none',
                            flexShrink: 0,
                            cursor: 'pointer',
                          }}
                        >
                          {category.name} ({itemCount})
                        </Button>
                      );
                    })}
                  </Box>
                </Box>
                
                {/* Items Grid */}
                <div id="items-container">
                  {isFetching ? (
                    <Grid container spacing={{ xs: 1, sm: 2 }}>
                      {[...Array(8)].map((_, index) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={index}>
                          <Skeleton variant="rectangular" height={itemCardHeight} sx={{ borderRadius: '12px' }} />
                        </Grid>
                      ))}
                    </Grid>
                  ) : filteredItems.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 8 } }}>
                      <MenuIcon sx={{ fontSize: { xs: 40, sm: 64 }, color: 'text.disabled', mb: 2 }} />
                      <Typography variant="h5" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.5rem' } }}>
                        No items found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {searchTerm ? 'Try adjusting your search terms' : `No items available in ${LOCATION_NAME} category`}
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: { 
                          xs: '1fr 1fr', 
                          sm: '1fr 1fr', 
                          md: '1fr 1fr 1fr', 
                          lg: `repeat(${isMobile ? 2 : 4}, 1fr)` 
                        },
                        gap: { xs: 1.5, sm: 2 },
                        alignItems: 'stretch',
                      }}>
                        {paginatedItems.map((item) => (
                          <MenuItemCard
                            key={item.id}
                            item={item}
                            onAddToCart={addToCart}
                            locationColor={LOCATION_COLOR}
                            accentColor={ACCENT_COLOR}
                            theme={theme}
                            isMobile={isMobile}
                            stockStatus={stockStatusMap[item.id]}
                            setSnackbar={setSnackbar}
                            isStockStatusLoaded={isStockStatusLoaded}
                            isStockStatusLoading={fetchingStockStatus}
                          />
                        ))}
                      </Box>
                      
                      {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 3, sm: 4 } }}>
                          <Pagination
                            count={totalPages}
                            page={page}
                            onChange={handlePageChange}
                            size={isMobile ? 'small' : 'medium'}
                            sx={{
                              '& .MuiPaginationItem-root': {
                                borderRadius: '6px',
                                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                              },
                              '& .Mui-selected': {
                                bgcolor: `${LOCATION_COLOR} !important`,
                                color: 'white',
                                '&:hover': {
                                  bgcolor: `${LOCATION_COLOR} !important`,
                                },
                              },
                            }}
                          />
                        </Box>
                      )}
                    </>
                  )}
                </div>
              </Box>
              
              {/* Cart Sidebar */}
              <Box sx={{
                width: { xs: cartWidth, md: 320 },
                minWidth: { xs: cartWidth, md: 320 },
                borderLeft: `1px solid ${alpha('#000', 0.06)}`,
                bgcolor: 'white',
                display: 'flex',
                flexDirection: 'column',
                height: 'calc(100% - 20px)',
                maxHeight: 'calc(100% - 20px)',
                marginTop: '20px',
                position: 'sticky',
                top: 0,
                right: 0,
                overflow: 'hidden',
                borderRadius: '12px 0 0 0',
                boxShadow: '-4px 0 20px rgba(0,0,0,0.03)',
                alignSelf: 'flex-start',
              }}>
                {/* Cart Header */}
                <Box sx={{
                  p: { xs: 1, md: 2.5 },
                  borderBottom: `1px solid ${alpha('#000', 0.06)}`,
                  bgcolor: 'white',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexShrink: 0,
                  minHeight: { xs: '50px', md: '70px' },
                }}>
                  <Typography variant="h6" sx={{
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 0.5, md: 1 },
                    fontSize: { xs: '0.9rem', md: '1.1rem' },
                  }}>
                    <CartIcon sx={{ color: ACCENT_COLOR, fontSize: { xs: '0.8rem', md: '1.2rem' } }} />
                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>Your Order</Box>
                    <Chip
                      label={`${getCartItemCount()}`}
                      size="small"
                      sx={{
                        bgcolor: hasOutOfStockInCart ? alpha('#f44336', 0.1) : alpha(ACCENT_COLOR, 0.1),
                        color: hasOutOfStockInCart ? '#f44336' : ACCENT_COLOR,
                        fontWeight: 600,
                        borderRadius: '4px',
                        fontSize: { xs: '0.8rem', md: '0.9rem' },
                        height: { xs: 20, md: 24 },
                        minWidth: { xs: 24, md: 'auto' },
                      }}
                    />
                  </Typography>
                  {cartItems.length > 0 && !isSmallMobile && (
                    <Button
                      size="small"
                      onClick={clearCart}
                      color="error"
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '6px',
                        fontSize: { xs: '0.5rem', md: '0.75rem' },
                        minWidth: 'auto',
                        px: { xs: 0.75, md: 1.5 },
                      }}
                      startIcon={<DeleteIcon fontSize="small" sx={{ fontSize: { xs: '0.6rem', md: '1rem' } }} />}
                    >
                      <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>Clear</Box>
                    </Button>
                  )}
                </Box>
                
                {/* Cart Items List */}
                <Box sx={{
                  flex: 1,
                  overflow: 'auto',
                  p: { xs: 0.5, md: 2 },
                  '&::-webkit-scrollbar': {
                    width: 4,
                  },
                  '&::-webkit-scrollbar-track': {
                    bgcolor: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    bgcolor: 'grey.300',
                    borderRadius: 2,
                  },
                }}>
                  {cartItems.length === 0 ? (
                    <Box sx={{
                      textAlign: 'center',
                      py: { xs: 2, md: 6 },
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                    }}>
                      <CartIcon sx={{ fontSize: { xs: 24, md: 48 }, color: 'text.disabled', opacity: 0.4 }} />
                      <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600, fontSize: { xs: '0.6rem', md: '1rem' } }}>
                        Cart empty
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.5rem', md: '0.85rem' } }}>
                        Add items
                      </Typography>
                    </Box>
                  ) : (
                    cartItems.map((item) => (
                      <CartItem
                        key={item.id}
                        item={item}
                        onUpdateQuantity={updateQuantity}
                        onRemove={removeFromCart}
                        locationColor={LOCATION_COLOR}
                        stockStatus={stockStatusMap[item.id]}
                        setSnackbar={setSnackbar}
                      />
                    ))
                  )}
                </Box>
                
                {/* Cart Total */}
                {cartItems.length > 0 && (
                  <Box sx={{
                    p: { xs: 0.75, md: 2 },
                    borderTop: `1px solid ${alpha('#000', 0.06)}`,
                    bgcolor: '#f8f9fa',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <Typography variant="body2" fontWeight={600} fontSize={{ xs: '0.85rem', md: '1rem' }}>
                      Total:
                    </Typography>
                    <Typography variant="h6" fontWeight={700} color={LOCATION_COLOR} fontSize={{ xs: '1rem', md: '1.25rem' }}>
                      {formatCurrency(getCartTotal())}
                    </Typography>
                  </Box>
                )}
                
                {/* Place Order Button */}
                <Box sx={{
                  p: { xs: 0.75, md: 2 },
                  borderTop: `1px solid ${alpha('#000', 0.06)}`,
                  bgcolor: 'white',
                  flexShrink: 0,
                }}>
                  <Button
                    fullWidth
                    variant="contained"
                    disabled={cartItems.length === 0 || isSubmittingOrder || !locationStatus.can_take_orders || hasOutOfStockInCart || !isStockStatusLoaded}
                    onClick={handlePlaceOrder}
                    sx={{
                      bgcolor: !locationStatus.can_take_orders || hasOutOfStockInCart || !isStockStatusLoaded ? 'grey.400' : LOCATION_COLOR,
                      '&:hover': { 
                        bgcolor: !locationStatus.can_take_orders || hasOutOfStockInCart || !isStockStatusLoaded ? 'grey.400' : LOCATION_COLOR, 
                        opacity: 0.9 
                      },
                      '&:disabled': { bgcolor: 'grey.300' },
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      py: { xs: 0.8, md: 1.5 },
                      fontSize: { xs: '0.9rem', md: '1rem' },
                    }}
                    startIcon={!isStockStatusLoaded ? 
                      <CircularProgress size={20} color="inherit" /> : 
                      <SendIcon sx={{ fontSize: { xs: '1rem', md: '1.2rem' } }} />
                    }
                  >
                    {!isStockStatusLoaded ? 'Loading stock...' :
                     !locationStatus.can_take_orders ? 'Location Closed' : 
                     hasOutOfStockInCart ? '⚠️ Remove Out-of-Stock Items' :
                     cartItems.length === 0 ? 'Add Items' : 'Order'}
                  </Button>
                </Box>
              </Box>
            </Box>
          </div>
        </div>
        <Footer />
      </div>
      
      {/* ============================================ */}
      {/* ORDER DIALOG */}
      {/* ============================================ */}
      <Dialog
        open={orderDialogOpen}
        onClose={handleCloseOrderDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 1,
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}>
          <CartIcon sx={{ color: ACCENT_COLOR }} />
          <Typography variant="h6" component="span" fontWeight={700}>
            Place Your Order - {LOCATION_NAME}
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ 
          pt: 3,
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'grey.300',
            borderRadius: 2,
          },
        }}>
          {/* Order Type Selection */}
          <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
            <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
              Order Type
            </FormLabel>
            <RadioGroup
              row
              value={orderType}
              onChange={(e) => setOrderType(e.target.value)}
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <FormControlLabel
                value="DINE_IN"
                control={<Radio sx={{ color: LOCATION_COLOR, '&.Mui-checked': { color: LOCATION_COLOR } }} />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TableRestaurantIcon sx={{ color: LOCATION_COLOR }} />
                    <Typography>Dine In</Typography>
                  </Box>
                }
                sx={{
                  border: `2px solid ${orderType === 'DINE_IN' ? LOCATION_COLOR : alpha('#000', 0.12)}`,
                  borderRadius: '12px',
                  px: 2,
                  py: 1,
                  flex: 1,
                  m: 0,
                  minWidth: { xs: '100%', sm: 'auto' },
                  '& .MuiFormControlLabel-label': {
                    width: '100%',
                  },
                }}
              />
              <FormControlLabel
                value="TAKEAWAY"
                control={<Radio sx={{ color: LOCATION_COLOR, '&.Mui-checked': { color: LOCATION_COLOR } }} />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon sx={{ color: '#1976D2' }} />
                    <Typography>Takeaway</Typography>
                  </Box>
                }
                sx={{
                  border: `2px solid ${orderType === 'TAKEAWAY' ? LOCATION_COLOR : alpha('#000', 0.12)}`,
                  borderRadius: '12px',
                  px: 2,
                  py: 1,
                  flex: 1,
                  m: 0,
                  minWidth: { xs: '100%', sm: 'auto' },
                  '& .MuiFormControlLabel-label': {
                    width: '100%',
                  },
                }}
              />
            </RadioGroup>
          </FormControl>
          
          {/* DINE IN - Table Cards Selection */}
          {orderType === 'DINE_IN' && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
                Select Table <span style={{ color: '#f44336' }}>*</span>
              </Typography>
              
              {loadingTables ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={30} />
                </Box>
              ) : tables.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 4, 
                  bgcolor: alpha('#ff9800', 0.08),
                  borderRadius: '12px',
                  border: `1px dashed ${alpha('#ff9800', 0.3)}`,
                }}>
                  <TableRestaurantIcon sx={{ fontSize: 40, color: '#ff9800', opacity: 0.5 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No available tables at the moment
                  </Typography>
                </Box>
              ) : (
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                  gap: 1.5,
                  maxHeight: '280px',
                  overflowY: 'auto',
                  pr: 0.5,
                  alignItems: 'start',
                  '&::-webkit-scrollbar': {
                    width: 3,
                  },
                  '&::-webkit-scrollbar-thumb': {
                    bgcolor: 'grey.300',
                    borderRadius: 2,
                  },
                }}>
                  {tables.map((table) => {
                    const isSelected = selectedTable === table.id;
                    return (
                      <Card
                        key={table.id}
                        onClick={() => setSelectedTable(table.id)}
                        sx={{
                          cursor: 'pointer',
                          border: `2px solid ${isSelected ? LOCATION_COLOR : alpha('#000', 0.08)}`,
                          borderRadius: '12px',
                          bgcolor: isSelected ? alpha(LOCATION_COLOR, 0.05) : 'white',
                          transition: 'all 0.2s ease',
                          height: '100%',
                          minHeight: '100px',
                          display: 'flex',
                          flexDirection: 'column',
                          '&:hover': {
                            borderColor: LOCATION_COLOR,
                            boxShadow: theme.shadows[2],
                            transform: 'translateY(-2px)',
                          },
                          '&:active': {
                            transform: 'scale(0.96)',
                          },
                          position: 'relative',
                          overflow: 'visible',
                        }}
                      >
                        {isSelected && (
                          <Box sx={{
                            position: 'absolute',
                            top: -6,
                            right: -6,
                            bgcolor: LOCATION_COLOR,
                            color: 'white',
                            borderRadius: '50%',
                            width: 22,
                            height: 22,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: `0 2px 8px ${alpha(LOCATION_COLOR, 0.3)}`,
                          }}>
                            <CheckCircleIcon sx={{ fontSize: 14 }} />
                          </Box>
                        )}
                        
                        <CardContent sx={{ 
                          p: 1.5,
                          textAlign: 'center',
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          '&:last-child': { pb: 1.5 },
                        }}>
                          <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.5,
                            width: '100%',
                          }}>
                            <TableRestaurantIcon sx={{
                              fontSize: 32,
                              color: isSelected ? LOCATION_COLOR : 'text.secondary',
                            }} />
                            <Typography variant="body1" fontWeight={isSelected ? 800 : 700} sx={{
                              color: isSelected ? LOCATION_COLOR : 'text.primary',
                              fontSize: '0.9rem',
                              lineHeight: 1.2,
                              letterSpacing: '0.3px',
                            }}>
                              {table.table_name}
                            </Typography>
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              mt: 0.5,
                            }}>
                              <PeopleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                {table.capacity || 'N/A'} seats
                              </Typography>
                            </Box>
                            {table.is_occupied === 1 && (
                              <Chip
                                label="Occupied"
                                size="small"
                                color="error"
                                sx={{ 
                                  fontSize: '0.55rem', 
                                  height: 18,
                                  fontWeight: 700,
                                  '& .MuiChip-label': { px: 0.75 }
                                }}
                              />
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Box>
              )}
              
              {selectedTable && (
                <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon sx={{ color: 'success.main', fontSize: '1rem' }} />
                  <Typography variant="caption" color="success.main" fontWeight={700}>
                    Table {tables.find(t => t.id === selectedTable)?.table_name || selectedTable} selected
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          
          {/* TAKEAWAY - Phone Input */}
          {orderType === 'TAKEAWAY' && (
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Customer Phone Number"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                placeholder="Enter phone number (optional)"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon sx={{ color: '#94a3b8' }} />
                    </InputAdornment>
                  ),
                }}
                helperText="Phone number is optional for takeaway orders"
              />
            </Box>
          )}
          
          {/* Order Notes */}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Order Notes (Optional)"
              value={orderNotes}
              onChange={(e) => setOrderNotes(e.target.value)}
              placeholder="Special instructions, allergies, etc."
              multiline
              rows={2}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                },
              }}
            />
          </Box>
        </DialogContent>
        
        <DialogActions sx={{
          borderTop: `1px solid ${alpha('#000', 0.06)}`,
          pt: 2,
          px: 3,
          pb: 3,
          gap: 1,
          flexWrap: 'wrap',
        }}>
          <Button
            onClick={handleCloseOrderDialog}
            disabled={isSubmittingOrder}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitOrder}
            variant="contained"
            disabled={isSubmittingOrder || cartItems.length === 0 || !locationStatus.can_take_orders || hasOutOfStockInCart || !isStockStatusLoaded}
            sx={{
              bgcolor: !locationStatus.can_take_orders || hasOutOfStockInCart || !isStockStatusLoaded ? 'grey.400' : LOCATION_COLOR,
              '&:hover': { 
                bgcolor: !locationStatus.can_take_orders || hasOutOfStockInCart || !isStockStatusLoaded ? 'grey.400' : LOCATION_COLOR, 
                opacity: 0.9 
              },
              '&:disabled': { bgcolor: 'grey.300' },
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 4,
              py: 1,
            }}
            startIcon={isSubmittingOrder ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
          >
            {!isStockStatusLoaded ? 'Loading stock...' :
             !locationStatus.can_take_orders ? 'Location Closed' : 
             hasOutOfStockInCart ? '⚠️ Remove Out-of-Stock Items' :
             isSubmittingOrder ? 'Sending...' : 'Send to Cashier'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* ============================================ */}
      {/* RECEIPT DIALOG */}
      {/* ============================================ */}
      <Dialog
        open={receiptDialogOpen}
        onClose={handleCloseReceiptDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 1,
            maxWidth: '400px',
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
          pb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#f8f9fa',
          flexWrap: 'wrap',
          gap: 1,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <ReceiptIcon sx={{ color: LOCATION_COLOR }} />
            <Typography variant="h6" component="span" fontWeight={700}>
              Order Receipt
            </Typography>
            {receiptData && (
              <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                {kotTicketData && (
                  <Chip
                    label="KOT"
                    size="small"
                    sx={{
                      bgcolor: '#d4edda',
                      color: '#155724',
                      fontWeight: 'bold',
                      fontSize: '0.6rem',
                      height: '20px',
                      border: '1px solid #155724',
                    }}
                  />
                )}
                {botTicketData && (
                  <Chip
                    label="BOT"
                    size="small"
                    sx={{
                      bgcolor: '#cce5ff',
                      color: '#004085',
                      fontWeight: 'bold',
                      fontSize: '0.6rem',
                      height: '20px',
                      border: '1px solid #004085',
                    }}
                  />
                )}
              </Box>
            )}
          </Box>
          <IconButton
            onClick={handleCloseReceiptDialog}
            sx={{ color: 'text.secondary' }}
          >
            <ClearIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ 
          pt: 3, 
          backgroundColor: '#f5f7fa',
          maxHeight: '70vh',
          overflowY: 'auto',
        }}>
          {/* Print Progress */}
          {isPrinting && printProgress > 0 && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" fontWeight={600}>
                  Printing...
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                  {printProgress}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={printProgress} 
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  bgcolor: alpha(LOCATION_COLOR, 0.1),
                  '& .MuiLinearProgress-bar': {
                    bgcolor: LOCATION_COLOR,
                  }
                }}
              />
            </Box>
          )}
          
          {/* Ticket Information */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: 'text.secondary' }}>
              Select tickets to print:
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* KOT Ticket */}
              {kotTicketData && (
                <Paper 
                  elevation={0}
                  sx={{
                    p: 2,
                    border: '2px solid #155724',
                    borderRadius: '12px',
                    bgcolor: '#f0fff4',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ 
                      bgcolor: '#d4edda', 
                      p: 1, 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <KitchenIcon sx={{ color: '#155724' }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#155724' }}>
                        Kitchen Order Ticket (KOT)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {kotTicketData.items.length} items · {formatCurrency(kotTicketData.total)}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handlePrintKOT}
                    disabled={isPrinting}
                    startIcon={isPrinting ? <CircularProgress size={16} color="inherit" /> : <PrintIcon />}
                    sx={{
                      bgcolor: '#155724',
                      '&:hover': { bgcolor: '#0d3b1f' },
                      textTransform: 'none',
                      borderRadius: '8px',
                      fontWeight: 600,
                    }}
                  >
                    Print KOT
                  </Button>
                </Paper>
              )}
              
              {/* BOT Ticket */}
              {botTicketData && (
                <Paper 
                  elevation={0}
                  sx={{
                    p: 2,
                    border: '2px solid #004085',
                    borderRadius: '12px',
                    bgcolor: '#f0f8ff',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ 
                      bgcolor: '#cce5ff', 
                      p: 1, 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <LocalBarIcon sx={{ color: '#004085' }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#004085' }}>
                        Bar Order Ticket (BOT)
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {botTicketData.items.length} items · {formatCurrency(botTicketData.total)}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handlePrintBOT}
                    disabled={isPrinting}
                    startIcon={isPrinting ? <CircularProgress size={16} color="inherit" /> : <PrintIcon />}
                    sx={{
                      bgcolor: '#004085',
                      '&:hover': { bgcolor: '#002b5c' },
                      textTransform: 'none',
                      borderRadius: '8px',
                      fontWeight: 600,
                    }}
                  >
                    Print BOT
                  </Button>
                </Paper>
              )}
            </Box>
          </Box>
          
          {/* Order Summary */}
          {receiptData && (
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              bgcolor: 'white', 
              borderRadius: '8px', 
              border: '1px solid #e0e0e0' 
            }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary">
                Order #{receiptData.order_number}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Total Items: {orderTotalItems || 0}
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  Total: {formatCurrency(orderTotalAmount || 0)}
                </Typography>
              </Box>
              {kotTicketData && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, borderTop: '1px dashed #e0e0e0', pt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    🍳 Kitchen:
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {kotTicketData.items.reduce((sum, item) => sum + item.quantity, 0)} items · {formatCurrency(kotTicketData.total)}
                  </Typography>
                </Box>
              )}
              {botTicketData && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    🍺 Bar:
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {botTicketData.items.reduce((sum, item) => sum + item.quantity, 0)} items · {formatCurrency(botTicketData.total)}
                  </Typography>
                </Box>
              )}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                mt: 1, 
                pt: 1, 
                borderTop: `2px solid ${LOCATION_COLOR}`,
              }}>
                <Typography variant="body2" fontWeight={700}>
                  GRAND TOTAL
                </Typography>
                <Typography variant="body2" fontWeight={700} color={LOCATION_COLOR}>
                  {formatCurrency(orderTotalAmount || 0)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{
          borderTop: `1px solid ${alpha('#000', 0.06)}`,
          pt: 2,
          px: 3,
          pb: 3,
          gap: 1,
          bgcolor: '#f8f9fa',
          flexWrap: 'wrap',
        }}>
          <Button
            onClick={handleCloseReceiptDialog}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 3,
            }}
          >
            Close
          </Button>
          {(kotTicketData || botTicketData) && (
            <Button
              variant="outlined"
              onClick={handlePrintReceipt}
              disabled={isPrinting}
              startIcon={isPrinting ? <CircularProgress size={20} /> : <PrintIcon />}
              sx={{
                borderColor: LOCATION_COLOR,
                color: LOCATION_COLOR,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
                '&:hover': {
                  borderColor: LOCATION_COLOR,
                  bgcolor: alpha(LOCATION_COLOR, 0.05),
                }
              }}
            >
              {isPrinting ? 'Printing...' : 'Print All'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={8000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: '8px', whiteSpace: 'pre-line' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      
      <style>
        {`
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0; }
          }
          @media print {
            .no-print {
              display: none !important;
            }
          }
        `}
      </style>
    </>
  );
};

export default LocationWaitersDashboard;